import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useState, useMemo } from "react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Loader2 } from "lucide-react";

import { useRouteParam } from "@/lib/use-route-param";
import { PageHeader, PageHeaderSkeleton } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TimeRangeSelect } from "@/components/admin/time-range-select";
import { CostBarChart } from "@/components/admin/cost-bar-chart";
import { CostTrendChart } from "@/components/admin/cost-trend-chart";
import { AppLink } from "@/components/app-link";
import {
  formatCost,
  formatOperationType,
  getTimeRangeBounds,
} from "@/lib/cost";
import type { TimeRange } from "@/lib/cost";

type OrgDetail = {
  orgName: string;
  totalCost: number;
  totalOps: number;
  projectBreakdown: Array<{
    projectId: string;
    projectName: string;
    totalCost: number;
    opCount: number;
  }>;
  typeBreakdown: Array<{ type: string; totalCost: number; opCount: number }>;
};

type TimeSeriesRow = { month: string; totalCost: number; opCount: number };

export const Route = createFileRoute("/admin/orgs/$orgId")({
  component: OrgDetailPage,
});

function OrgDetailPage() {
  const orgId = useRouteParam("orgId");
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const bounds = useMemo(() => getTimeRangeBounds(timeRange), [timeRange]);

  const {
    data: orgDetailData,
    error: orgDetailError,
    isPending: isOrgDetailPending,
  } = useQuery(
    convexQuery(
      api.admin.platform.orgDetail,
      orgId ? { organizationId: orgId as Id<"organizations">, ...bounds } : "skip"
    )
  );
  const {
    data: timeSeriesData,
    error: timeSeriesError,
    isPending: isTimeSeriesPending,
  } = useQuery(
    convexQuery(
      api.admin.platform.orgCostTimeSeries,
      orgId ? { organizationId: orgId as Id<"organizations">, ...bounds } : "skip"
    )
  );

  const orgDetail = orgDetailData as OrgDetail | undefined;
  const timeSeries = timeSeriesData as TimeSeriesRow[] | undefined;

  if (!orgId) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Invalid route — organization ID is missing.
      </div>
    );
  }

  if (orgDetailError || timeSeriesError) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Failed to load organization data.
      </div>
    );
  }

  if (isOrgDetailPending || isTimeSeriesPending) {
    return (
      <div className="space-y-4">
        <PageHeaderSkeleton withTrail />
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!orgDetail || !timeSeries) {
    return (
      <div className="flex items-center justify-center py-24 text-muted-foreground">
        Failed to load organization data.
      </div>
    );
  }

  const projectBarData = orgDetail.projectBreakdown.map((p) => ({
    name: p.projectName,
    rawCost: p.totalCost,
  }));

  const typeBarData = orgDetail.typeBreakdown.map((t) => ({
    name: formatOperationType(t.type),
    rawCost: t.totalCost,
  }));

  return (
    <div className="space-y-4">
      <PageHeader
        title={orgDetail.orgName}
        trail={[{ label: "Cost Dashboard", href: "/admin" }]}
        actions={
          <TimeRangeSelect value={timeRange} onChange={setTimeRange} />
        }
      />

      {/* Summary */}
      <div className="rounded-lg border-2 border-border bg-card px-6 py-4">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
          Organization Total Cost
        </p>
        <p className="mt-1 text-3xl font-bold tracking-tight text-foreground">
          {formatCost(orgDetail.totalCost)}
        </p>
        <p className="text-sm text-muted-foreground">
          {orgDetail.totalOps} completed operation{orgDetail.totalOps !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Charts */}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost by Project</CardTitle>
          </CardHeader>
          <CardContent>
            <CostBarChart
              data={projectBarData}
              height={Math.max(200, projectBarData.length * 50)}
            />
          </CardContent>
        </Card>

        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Monthly Cost Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <CostTrendChart data={timeSeries} />
          </CardContent>
        </Card>
      </div>

      {/* Cost by Type */}
      {orgDetail.typeBreakdown.length > 0 && (
        <Card className="py-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cost by Operation Type</CardTitle>
          </CardHeader>
          <CardContent>
            <CostBarChart data={typeBarData} height={Math.max(150, typeBarData.length * 50)} />
          </CardContent>
        </Card>
      )}

      {/* Projects Table */}
      <Card className="py-4">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Projects</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-bold uppercase tracking-wider text-xs">
                    Project
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Operations
                  </TableHead>
                  <TableHead className="font-bold uppercase tracking-wider text-xs text-right">
                    Total Cost
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {orgDetail.projectBreakdown.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                      No project cost data yet
                    </TableCell>
                  </TableRow>
                ) : (
                  orgDetail.projectBreakdown.map((project) => (
                    <TableRow
                      key={project.projectId}
                      className="hover:bg-muted/50"
                    >
                      <TableCell>
                        {project.projectId !== "unassigned" ? (
                          <AppLink
                            to={`/admin/orgs/${orgId}/projects/${project.projectId}`}
                            className="font-medium text-foreground hover:underline"
                          >
                            {project.projectName}
                          </AppLink>
                        ) : (
                          <span className="font-medium italic text-muted-foreground">
                            {project.projectName}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">{project.opCount}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {formatCost(project.totalCost)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
